/**
 * Hook to manage HTML direction (LTR/RTL) based on current language
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Languages that use RTL (Right-to-Left) direction
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur', 'yi', 'ji'];

/**
 * Custom hook that automatically sets the HTML dir attribute
 * based on the current i18n language
 * @returns The current direction ('ltr' or 'rtl')
 */
export const useDirection = (): 'ltr' | 'rtl' => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Determine if current language is RTL
  const isRTL = RTL_LANGUAGES.includes(currentLanguage);
  const direction = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    // Set the dir attribute on the HTML element
    document.documentElement.dir = direction;

    // Also set on body for legacy support
    document.body.dir = direction;
  }, [direction, currentLanguage]);

  return direction;
};

/**
 * Utility function to check if a language is RTL
 * @param language Language code (e.g., 'ar', 'en', 'he')
 * @returns true if the language is RTL
 */
export const isRTLLanguage = (language: string): boolean => {
  return RTL_LANGUAGES.includes(language);
};

/**
 * Utility function to get direction for a specific language
 * @param language Language code
 * @returns 'ltr' or 'rtl'
 */
export const getLanguageDirection = (language: string): 'ltr' | 'rtl' => {
  return isRTLLanguage(language) ? 'rtl' : 'ltr';
};
